import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppSecurityRight, EnvironmentConfiguration, TenantInfoRequest, TenantStatus, UserType } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { UtilService } from '@jms/utils/util.services';
import { ApplicationActions } from '@jms/tenant/module.jms.state';
import { ContextService } from '@jms/ui/ui.standalone.services/context.service';
import { AuthTokenService, OfflineError, PORTAL_AUTH_TOKEN_KEY } from '@jms/utils/util.authentication';
import { filter, firstValueFrom, take } from 'rxjs';
// logged-in.guard.ts

@Injectable()
export class AuthGuard {
  private token: string;
  private tenantInfo: TenantInfoRequest;
  private environment: EnvironmentConfiguration;

  constructor(
    private router: Router,
    private location: Location,
    private context: ContextService,
    private actions: ApplicationActions,
    private jwt: JwtHelperService,
    private authTokenService: AuthTokenService
  ) {
    actions.token$.subscribe(x => (this.token = x));
    actions.tenantInfo$.subscribe(x => (this.tenantInfo = x));
    this.environment = ConfigService.environment;
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

    const url = this.location.path();

    if (!url.toLowerCase().startsWith('/callback')) {
      const token = UtilService.getURLParameter('token', url);
      if (token) {
        let stateUrl = state.url;
        const index = stateUrl.indexOf('?');
        if (index >= 0) {
          stateUrl = stateUrl.substring(0, index);
        }
        const queryParams = {
          queryParams: {
            token: token,
            returnUrl: stateUrl
          }
        };
        this.router.navigate([`/callback`], queryParams);
        return false;
      }
    }

    if (this.token && !this.jwt.isTokenExpired(this.token)) {
      return this.continueWithToken(this.token, state.url);
    }

    // Expired (or near-expiry) local session — try cookie refresh via AuthTokenService
    // (handles offline, multi-tab lock, status-0). Do not kick to id login on network blips
    // after laptop wake; only clear + redirect when refresh truly rejects the session.
    if (this.token) {
      try {
        const refreshed = await this.authTokenService.getValidToken();

        if (refreshed) {
          this.actions.loginAuthorised(refreshed, undefined, undefined, state.url, true);
          return this.continueWithToken(refreshed, state.url);
        }
      } catch (error) {
        if (error instanceof OfflineError || (error instanceof HttpErrorResponse && error.status === 0)) {
          // Network unavailable (common on laptop wake). Keep local session; AuthTokenService
          // retries on 'online' / focus. Allow the app through so tabs are not sent to bare id login.
          return this.continueWithToken(this.token, state.url);
        }
      }
    }

    const callback = `${this.context.origin}${state.url}`;
    this.authTokenService.clear();
    // Redirect the user before denying them access to this route, carrying the tenant's SSO hint.
    UtilService.navigate(this.environment.jms.logoutHref, callback, UtilService.ssoHintParams(this.environment.sso));
    return false;
  }

  /**
   * Token is valid — allow the *original* navigation to finish once tenant info is available.
   *
   * Previously missing tenant info did getTenantInfo + return false, then an effect re-entered via
   * navigateByUrl(returnUrl). That second hop dropped secondary outlets (e.g. inspector on F5).
   * Awaiting info and returning true keeps the browser URL's named outlets on the first activation.
   */
  private async continueWithToken(token: string, stateUrl: string): Promise<boolean> {

    const portalRedirect = this.getPortalRedirect(token);

    if (portalRedirect === 'deny') {
      return this.accessDenied();
    }

    if (portalRedirect) {
      this.redirectToPortal(portalRedirect, token);
      return false;
    }

    if (!this.tenantInfo) {
      await this.ensureTenantInfo(stateUrl);
    }

    // Non-ready tenants are redirected by checkTenantStatus; do not activate the requested route.
    const status = this.tenantInfo?.tenantStatus;
    if (status === TenantStatus.Created || status === TenantStatus.Confirmed || status === TenantStatus.Initializing) {
      return false;
    }

    return true;
  }

  /**
   * Load tenant info for a cold boot. returnUrl is stored for non-ready redirects / legacy re-entry;
   * prefer the address bar path so named outlets and query params are never stripped from the stamp.
   */
  private async ensureTenantInfo(stateUrl: string): Promise<void> {

    if (this.tenantInfo) return;

    const returnUrl = this.location.path() || stateUrl || '/home';
    this.actions.getTenantInfo(returnUrl, true);

    await firstValueFrom(this.actions.tenantInfo$.pipe(filter((info): info is TenantInfoRequest => !!info), take(1)));
  }

  private getPortalRedirect(token: string): '/clients/' | '/contractors/' | '/portal-select' | 'deny' | null {

    const user = this.jwt.decodeToken(token) as { role?: { type?: UserType; rights?: number[] }; tenants?: Array<{ role?: { type?: UserType; rights?: number[] }; tenantUrl?: string; url?: string; tenant?: { url?: string } }> };
    const tenant = this.getCurrentTenant(user?.tenants ?? []);
    const role = tenant?.role ?? user?.role;

    if (role?.type !== UserType.LoginContact) return null;

    // Portal login contacts may only enter when the tenant has the enterprise portal provisioned;
    // otherwise deny — they have no place in the main tenant app.
    if (this.environment?.enterprisePortal !== true) return 'deny';

    const rights = role?.rights ?? [];
    const canAccessClientPortal = rights.includes(AppSecurityRight.LoginClient);
    const canAccessContractorPortal = rights.includes(AppSecurityRight.LoginContractor);

    switch (true) {
      case canAccessClientPortal && canAccessContractorPortal:
        return '/portal-select';

      case canAccessClientPortal:
        return '/clients/';

      case canAccessContractorPortal:
        return '/contractors/';

      default:
        return null;
    }
  }

  private redirectToPortal(path: '/clients/' | '/contractors/' | '/portal-select', token: string): void {

    if (path === '/portal-select') {
      this.router.navigate([path]);
      return;
    }

    // Path-mode portals share this origin's localStorage but read their session from
    // portal_auth_token, so seed it before handing over. The token already carries the
    // matching portal login right — getPortalRedirect only selects a portal path for it.
    localStorage.setItem(PORTAL_AUTH_TOKEN_KEY, token);
    window.location.assign(`${this.context.origin}${path}`);
  }

  /** Deny access and send the user to the access-denied page. Always returns false to block the guarded route. */
  private accessDenied(): boolean {

    this.router.navigate(['/login/access-denied']);
    return false;
  }

  private getCurrentTenant(tenants: Array<{ tenantUrl?: string; url?: string; tenant?: { url?: string } }>): { role?: { type?: UserType; rights?: number[] } } | null {

    const currentHost = this.context.web.toLowerCase();
    return tenants.find(tenant => this.getTenantUrl(tenant) === currentHost) ?? (tenants.length === 1 ? tenants[0] as any : null);
  }

  private getTenantUrl(tenant: { tenantUrl?: string; url?: string; tenant?: { url?: string } }): string {

    return (tenant?.tenantUrl ?? tenant?.url ?? tenant?.tenant?.url ?? '').toLowerCase();
  }
}
